/**
 * Product Service - Firebase Database Integration
 *
 * Handles all product-related operations with Firebase Realtime Database
 */

import { database } from '../firebase';
import { ref, get, set, update, remove, query, orderByChild, equalTo } from 'firebase/database';

/**
 * Get all products from Firebase
 * @returns {Promise<Array>} Array of products
 */
export const getAllProducts = async () => {
  try {
    const productsRef = ref(database, 'clothes');
    const snapshot = await get(productsRef);

    if (!snapshot.exists()) {
      console.log('📦 No products found in database');
      return [];
    }

    const productsData = snapshot.val();

    // Convert object to array with IDs
    const productsArray = Object.keys(productsData).map(key => ({
      id: key,
      ...productsData[key]
    }));

    console.log(`✅ Loaded ${productsArray.length} products from Firebase`);
    return productsArray;

  } catch (error) {
    console.error('❌ Error loading products:', error);
    throw new Error('Failed to load products from database');
  }
};

/**
 * Get single product by ID
 * @param {string} productId - Product ID
 * @returns {Promise<Object|null>} Product object or null
 */
export const getProductById = async (productId) => {
  try {
    if (!productId) {
      throw new Error('Product ID is required');
    }

    const productRef = ref(database, `clothes/${productId}`);
    const snapshot = await get(productRef);

    if (!snapshot.exists()) {
      console.log(`📦 Product not found: ${productId}`);
      return null;
    }

    const product = {
      id: productId,
      ...snapshot.val()
    };

    console.log(`✅ Loaded product: ${product.name}`);
    return product;

  } catch (error) {
    console.error(`❌ Error loading product ${productId}:`, error);
    throw error;
  }
};

/**
 * Get product by barcode
 * @param {string} barcode - Product barcode
 * @returns {Promise<Object|null>} Product object or null
 */
export const getProductByBarcode = async (barcode) => {
  try {
    if (!barcode) {
      throw new Error('Barcode is required');
    }

    console.log(`🔍 Searching for barcode: ${barcode}`);

    const productsRef = ref(database, 'clothes');
    const barcodeQuery = query(productsRef, orderByChild('barcode'), equalTo(barcode));
    const snapshot = await get(barcodeQuery);

    if (!snapshot.exists()) {
      console.log(`📦 No product found with barcode: ${barcode}`);
      return null;
    }

    // Get first matching product
    const productsData = snapshot.val();
    const productId = Object.keys(productsData)[0];
    const product = {
      id: productId,
      ...productsData[productId]
    };

    console.log(`✅ Found product: ${product.name} (barcode: ${barcode})`);
    return product;

  } catch (error) {
    console.error(`❌ Error searching barcode ${barcode}:`, error);
    throw error;
  }
};

/**
 * Add new product to Firebase
 * @param {Object} productData - Product data
 * @returns {Promise<string>} New product ID
 */
export const addProduct = async (productData) => {
  try {
    // Generate unique ID
    const productId = `prod_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    // Validate required fields
    if (!productData.name || !productData.price) {
      throw new Error('Product name and price are required');
    }

    const productRef = ref(database, `clothes/${productId}`);

    const newProduct = {
      ...productData,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    await set(productRef, newProduct);

    console.log(`✅ Product added: ${productId}`);
    return productId;

  } catch (error) {
    console.error('❌ Error adding product:', error);
    throw error;
  }
};

/**
 * Update existing product
 * @param {string} productId - Product ID
 * @param {Object} updates - Fields to update
 * @returns {Promise<void>}
 */
export const updateProduct = async (productId, updates) => {
  try {
    if (!productId) {
      throw new Error('Product ID is required');
    }

    const productRef = ref(database, `clothes/${productId}`);

    const updateData = {
      ...updates,
      updatedAt: new Date().toISOString()
    };

    await update(productRef, updateData);

    console.log(`✅ Product updated: ${productId}`);

  } catch (error) {
    console.error(`❌ Error updating product ${productId}:`, error);
    throw error;
  }
};

/**
 * Delete product
 * @param {string} productId - Product ID
 * @returns {Promise<void>}
 */
export const deleteProduct = async (productId) => {
  try {
    if (!productId) {
      throw new Error('Product ID is required');
    }

    const productRef = ref(database, `clothes/${productId}`);
    await remove(productRef);

    console.log(`✅ Product deleted: ${productId}`);

  } catch (error) {
    console.error(`❌ Error deleting product ${productId}:`, error);
    throw error;
  }
};

/**
 * Search products by name or description
 * @param {string} searchQuery - Search query
 * @returns {Promise<Array>} Matching products
 */
export const searchProducts = async (searchQuery) => {
  try {
    if (!searchQuery) {
      return getAllProducts();
    }

    const allProducts = await getAllProducts();
    const query = searchQuery.toLowerCase();

    const results = allProducts.filter(product => {
      return (
        product.name?.toLowerCase().includes(query) ||
        product.description?.toLowerCase().includes(query) ||
        product.category?.toLowerCase().includes(query) ||
        product.brand?.toLowerCase().includes(query) ||
        product.barcode?.includes(query)
      );
    });

    console.log(`🔍 Search "${searchQuery}": ${results.length} results`);
    return results;

  } catch (error) {
    console.error('❌ Error searching products:', error);
    throw error;
  }
};

/**
 * Get products by category
 * @param {string} category - Category name
 * @returns {Promise<Array>} Products in category
 */
export const getProductsByCategory = async (category) => {
  try {
    const allProducts = await getAllProducts();

    const results = allProducts.filter(product =>
      product.category?.toLowerCase() === category.toLowerCase()
    );

    console.log(`📁 Category "${category}": ${results.length} products`);
    return results;

  } catch (error) {
    console.error(`❌ Error loading category ${category}:`, error);
    throw error;
  }
};

/**
 * Check if product exists by barcode
 * @param {string} barcode - Barcode to check
 * @returns {Promise<boolean>} True if exists
 */
export const productExistsByBarcode = async (barcode) => {
  try {
    const product = await getProductByBarcode(barcode);
    return !!product;
  } catch (error) {
    console.error('❌ Error checking barcode:', error);
    return false;
  }
};

/**
 * Get catalog products (fonte = piazza_italia, adatto_per_prova = true)
 * Uses a Firebase query to avoid downloading all products
 * @returns {Promise<Array>} Catalog products
 */
export const getCatalogProducts = async () => {
  try {
    const productsRef = ref(database, 'clothes');
    const catalogQuery = query(productsRef, orderByChild('fonte'), equalTo('piazza_italia'));
    const snapshot = await get(catalogQuery);

    if (!snapshot.exists()) {
      return [];
    }

    const data = snapshot.val();
    const products = Object.keys(data)
      .map(key => ({ id: key, ...data[key] }))
      .filter(p => p.adatto_per_prova);

    return products;
  } catch (error) {
    console.error('Error loading catalog products:', error);
    throw error;
  }
};

const productService = {
  getAllProducts,
  getProductById,
  getProductByBarcode,
  addProduct,
  updateProduct,
  deleteProduct,
  searchProducts,
  getProductsByCategory,
  productExistsByBarcode,
  getCatalogProducts
};

export default productService;
